import { useState } from "react";
import { X, Crosshair, Loader2, Route as RouteIcon, ShieldCheck, MapPin, Navigation } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { riskLevel, roadReasons, WEATHER_CODES } from "@/lib/params";
import { CitySearch } from "@/components/weather/TopBar";
import { routeIncidents, delayAhead } from "@/lib/traffic";
import { REPORT_TYPES } from "@/lib/reportTypes";

const Endpoint = ({ label, value, onChange, testid, near }) => {
  const { t } = useI18n();
  const gps = () => {
    if (!navigator.geolocation) return toast.error(t("Tarayıcınız konum desteklemiyor"));
    navigator.geolocation.getCurrentPosition(
      (p) => onChange({ name: t("Konumum"), lat: p.coords.latitude, lon: p.coords.longitude }),
      () => toast.error(t("Konum izni alınamadı"))
    );
  };
  return (
    <div className="space-y-1">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      {value ? (
        <div data-testid={`${testid}-value`} className="flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-3 py-2 text-sm text-sky-100">
          <MapPin size={14} className="text-sky-400" /><span className="flex-1 truncate">{value.name}</span>
          <button data-testid={`${testid}-clear`} onClick={() => onChange(null)} className="text-slate-400 hover:text-white"><X size={14} /></button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <CitySearch onPick={onChange} testid={testid} placeholder={label} near={near} />
          <button data-testid={`${testid}-gps`} onClick={gps} title={t("Konumum")} className="shrink-0 rounded-full border border-white/10 p-2 text-sky-300 hover:bg-white/5"><Crosshair size={15} /></button>
        </div>
      )}
    </div>
  );
};

const DepartureBars = ({ data, depart, onPick }) => {
  const { t } = useI18n();
  return (
    <div>
      <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("Kalkış saatine göre risk (24 saat)")}</div>
      <div data-testid="route-departure-bars" className="flex h-12 items-end gap-[3px]">
        {data.departures.map((d) => (
          <button key={d.offset} data-testid={`route-dep-bar-${d.offset}`} onClick={() => onPick(d.offset)} title={`${d.time.slice(11, 16)} · ${d.max_risk}`}
            className={`flex-1 rounded-sm transition-opacity hover:opacity-100 ${d.offset === depart ? "opacity-100 ring-1 ring-sky-300" : "opacity-70"}`}
            style={{ height: `${Math.max(8, d.max_risk)}%`, background: riskLevel(d.max_risk).color }} />
        ))}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-500">
        <span>{data.departures[0].time.slice(11, 16)}</span><span>{data.departures[23].time.slice(11, 16)}</span>
      </div>
    </div>
  );
};

const SampleRow = ({ s, onSelect }) => {
  const { t } = useI18n();
  const lvl = riskLevel(s.road), reasons = roadReasons(s);
  return (
    <button data-testid={`route-sample-${s.km}`} onClick={() => onSelect(s)} className="flex w-full gap-3 rounded-lg px-2 py-2 text-left hover:bg-white/5">
      <div className="flex flex-col items-center">
        <span className="h-3 w-3 rounded-full" style={{ background: lvl.color }} />
        <span className="mt-0.5 w-px flex-1 bg-white/10" />
      </div>
      <div className="flex-1">
        <div className="flex items-baseline justify-between">
          <span className="font-heading text-base font-bold text-white">{Math.round(s.km)} km</span>
          <span className="font-mono text-[11px] text-slate-400">{s.time.slice(11, 16)}</span>
        </div>
        <div className="text-xs text-slate-300">{t(WEATHER_CODES[s.weather_code] || "—")} · {Math.round(s.temperature_2m)}° · {s.precipitation?.toFixed(1)} mm</div>
        {reasons.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{reasons.map((r) => <span key={r} className="rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300">{t(r)}</span>)}</div>}
      </div>
    </button>
  );
};

const fmtDur = (h, t) => { const m = Math.round(h * 60); return m < 60 ? `${m} ${t("dk")}` : `${Math.floor(m / 60)} ${t("sa")} ${m % 60} ${t("dk")}`; };

const Traffic = ({ incidents, delay, onSelect }) => {
  const { t } = useI18n();
  if (!incidents.length) return <div data-testid="route-traffic-none" className="text-xs text-slate-500">{t("Rota üzerinde trafik bildirimi yok.")}</div>;
  return (
    <div data-testid="route-traffic" className="space-y-1.5 rounded-xl border border-orange-400/30 bg-orange-500/10 p-3">
      <div className="text-sm font-semibold text-orange-300">{t("Trafik gecikmesi")} +{Math.round(delay / 60)} {t("dk")} · {incidents.length} {t("bildirim")}</div>
      {incidents.slice(0, 5).map((i) => {
        const T = REPORT_TYPES[i.type];
        return (
          <button key={i.id} data-testid={`route-incident-${i.id}`} onClick={() => onSelect({ lat: i.lat, lon: i.lon })} className="flex w-full items-center gap-2 text-left text-xs text-slate-300 hover:text-white">
            <T.icon size={13} style={{ color: T.color }} /> <span className="flex-1">{t(T.label)}</span>
            <span className="font-mono text-slate-400">{Math.round(i.km)} km · +{Math.round(i.delay_s / 60)} {t("dk")}</span>
          </button>
        );
      })}
    </div>
  );
};

export const RoutePanel = ({ route, setRoute, onClose, onSelect, onStartNav, reports = [] }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const { from, to, depart, data } = route;
  const incidents = data ? routeIncidents(data.nav, reports) : [];
  const delay = data ? delayAhead(incidents, 0, data.duration_h * 3600) : 0;

  const calc = async (dep = depart) => {
    if (!from || !to) return toast.error(t("Başlangıç ve varış seçin"));
    setBusy(true);
    try {
      const r = await http.get("/route", { params: { from_lat: from.lat, from_lon: from.lon, to_lat: to.lat, to_lon: to.lon, depart: dep } });
      setRoute((x) => ({ ...x, depart: dep, data: r.data }));
    } catch (e) { toast.error(t(errMsg(e, "Rota bulunamadı"))); }
    setBusy(false);
  };
  const set = (k) => (v) => setRoute((x) => ({ ...x, [k]: v, data: null }));

  return (
    <aside data-testid="route-panel" className={`glass panel-enter absolute left-3 top-20 z-[1000] flex w-[calc(100%-24px)] flex-col gap-4 overflow-y-auto rounded-2xl p-4 sm:w-[320px] ${data ? "bottom-[190px] sm:bottom-[120px]" : "max-h-[calc(100%-290px)] sm:max-h-[calc(100%-200px)]"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading text-lg font-bold uppercase text-white"><RouteIcon size={18} className="text-sky-400" /> {t("Rota Hava Durumu")}</div>
        <button data-testid="route-panel-close" onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white"><X size={18} /></button>
      </div>
      <Endpoint label={t("Nereden")} value={from} onChange={set("from")} testid="route-from" />
      <Endpoint label={t("Nereye")} value={to} onChange={set("to")} testid="route-to" />
      <div className="flex items-end gap-2">
        <label className="flex-1 space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t("Kalkış")}</div>
          <select data-testid="route-depart-select" value={depart} onChange={(e) => (data ? calc(+e.target.value) : setRoute((x) => ({ ...x, depart: +e.target.value })))}
            className="w-full rounded-full border border-white/10 bg-black/30 px-3 py-2 text-sm text-slate-100 focus:outline-none">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h} className="bg-slate-900">{h === 0 ? t("Şimdi") : t("+{h} saat", { h })}</option>)}
          </select>
        </label>
        <button data-testid="route-calc-btn" onClick={() => calc()} disabled={busy}
          className="flex items-center gap-2 rounded-full bg-sky-400 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-sky-300 disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />} {t("Rotayı Hesapla")}
        </button>
      </div>
      {data && (
        <div data-testid="route-result" className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-white/[0.03] p-2"><div className="font-mono text-[10px] uppercase text-slate-500">{t("Mesafe")}</div><div data-testid="route-distance" className="font-heading text-xl font-bold text-white">{Math.round(data.distance_km)} km</div></div>
            <div className="rounded-xl bg-white/[0.03] p-2"><div className="font-mono text-[10px] uppercase text-slate-500">{t("Süre")}</div><div data-testid="route-duration" className="font-heading text-xl font-bold text-white">{fmtDur(data.duration_h + delay / 3600, t)}</div>{delay > 0 && <div data-testid="route-duration-normal" className="font-mono text-[10px] text-slate-500">{t("normalde")} {fmtDur(data.duration_h, t)}</div>}</div>
            <div className="rounded-xl bg-white/[0.03] p-2"><div className="font-mono text-[10px] uppercase text-slate-500">{t("En yüksek risk")}</div><div data-testid="route-max-risk" className="font-heading text-xl font-bold" style={{ color: riskLevel(data.max_risk).color }}>{t(riskLevel(data.max_risk).label)}</div></div>
          </div>
          <Traffic incidents={incidents} delay={delay} onSelect={onSelect} />
          <button data-testid="nav-start-btn" onClick={onStartNav}
            className="keep-white flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 py-3 font-heading text-lg font-bold uppercase tracking-wide text-white hover:bg-emerald-500">
            <Navigation size={18} /> {t("Navigasyonu Başlat")}
          </button>
          <div data-testid="route-best-departure" className="flex items-center gap-3 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3">
            <ShieldCheck size={20} className="text-emerald-400" />
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">{t("En güvenli kalkış")}</div>
              <div className="font-heading text-lg font-bold text-white">{data.best.offset === 0 ? t("Şimdi") : data.best.time.slice(11, 16)} · {t(riskLevel(data.best.max_risk).label)}</div>
            </div>
            {data.best.offset !== depart && (
              <button data-testid="route-pick-best-btn" onClick={() => calc(data.best.offset)} className="rounded-full bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/30">{t("Bu saati seç")}</button>
            )}
          </div>
          <DepartureBars data={data} depart={depart} onPick={(d) => calc(d)} />
          <div>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("Rota boyunca")}</div>
            {data.samples.map((s) => <SampleRow key={s.km} s={s} onSelect={onSelect} />)}
          </div>
          <button data-testid="route-clear-btn" onClick={() => setRoute({ from: null, to: null, depart: 0, data: null })} className="w-full rounded-full border border-white/10 py-2 text-xs text-slate-400 hover:bg-white/5">{t("Rotayı temizle")}</button>
        </div>
      )}
    </aside>
  );
};
